import React from "react";
import { useTranslation } from "react-i18next";
import NestedTable from "../components/NestedTable";
import {
  DECLARATION_FORM_DEBOUNCE,
  DeclarationGoodsItemProducedDocumentForm,
  FieldsRefs,
  SetDeclarationFormFieldRefFunction,
} from "../Declaration";
import { LanguageType } from "i18n";
import {
  FormDatePicker,
  FormTextField,
  MRTable,
  Typography,
} from "@periplus/ui-library";
import NestedTableFormId from "../components/NestedTable/NestedTableFormId";
import FormField from "../components/FormField/FormField";
import EdecDomainFormAutocomplete from "domain/edec/components/EdecDomainFormAutocomplete";
import NestedTableFormContainer from "../components/NestedTable/NestedTableFormContainer";
import { EdecData } from "domain/edec/useGetEdecMasterData";

interface Props {
  name: string;
  edecData: EdecData;
  onReturn: () => void;
  setFormFieldRef: SetDeclarationFormFieldRefFunction;
  onFormFieldFocus: (value: string) => void;
  onFormFieldBlur: () => void;
  pageContainerWidth: number;
  formFieldsRefs: React.MutableRefObject<FieldsRefs>;
  pageContainerHeight: number;
}

export default function ({
  name,
  edecData,
  onReturn,
  setFormFieldRef,
  onFormFieldFocus,
  onFormFieldBlur,
  pageContainerWidth,
  formFieldsRefs,
  pageContainerHeight,
}: Props) {
  const { t, i18n } = useTranslation("declarationNew");
  const language = i18n.language as LanguageType;

  const previousDocumentsColumns = React.useMemo<
    MRTable.MRT_ColumnDef<DeclarationGoodsItemProducedDocumentForm>[]
  >(
    () => [
      {
        header: t("Type"),
        accessorKey: "produced_document_type",
      },
      {
        header: t("Designation"),
        Cell: ({ row }) =>
          edecData.edec_domains["documentType"].find(
            (el) => el.value === row.original.produced_document_type
          )?.[`meaning_${language}`],
      },
      {
        header: t("Number"),
        accessorKey: "produced_document_reference_number",
      },
      {
        header: t("Date"),
        accessorKey: "produced_document_issue_date",
        Cell: ({ row }) =>
          row.original.produced_document_issue_date?.format("DD.MM.YYYY"),
      },
      {
        header: t("Addition"),
        accessorKey: "additional_information",
      },
    ],
    [t, language, edecData.edec_domains]
  );

  return (
    <NestedTable
      name={name}
      label={t("Document / Attestation")}
      columns={previousDocumentsColumns}
      defaultItem={{
        produced_document_type: null,
        produced_document_reference_number: null,
        produced_document_issue_date: null,
        additional_information: null,
      }}
      onReturn={onReturn}
      formFieldsRefs={formFieldsRefs}
      pageContainerHeight={pageContainerHeight}
      renderForm={({
        selectedItemRowNumber,
        getTableGeneralFormFieldProps,
        ref,
      }) => (
        <NestedTableFormContainer
          ref={ref}
          sx={(theme) => ({
            gridTemplateColumns:
              pageContainerWidth > theme.breakpoints.values.md
                ? "auto 1fr auto 1fr"
                : "auto 1fr",
            alignItems: "start",
          })}
        >
          <Typography>{t("Row Nr.")}</Typography>
          <NestedTableFormId id={selectedItemRowNumber} />
          <FormField
            {...getTableGeneralFormFieldProps("produced_document_type")}
            label={t("Type")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            sx={{
              gridColumn: "1",
            }}
            renderInput={({ inputRef, ...rest }) => (
              <EdecDomainFormAutocomplete
                {...rest}
                options={edecData.edec_domains["documentType"]}
                InputProps={{
                  inputRef,
                }}
              />
            )}
          />
          <FormField
            {...getTableGeneralFormFieldProps(
              "produced_document_reference_number"
            )}
            label={t("Number")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            fillByClickMode
            renderInput={({ ...rest }) => (
              <FormTextField {...rest} debounce={DECLARATION_FORM_DEBOUNCE} />
            )}
          />
          <FormField
            {...getTableGeneralFormFieldProps("produced_document_issue_date")}
            label={t("Date")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            renderInput={({ onFocus, onBlur, size, ...rest }) => (
              <FormDatePicker
                {...rest}
                slotProps={{
                  field: {
                    clearable: true,
                  },
                  textField: {
                    onFocus,
                    onBlur,
                    size,
                  },
                }}
              />
            )}
          />
          <FormField
            {...getTableGeneralFormFieldProps("additional_information")}
            label={t("Addition")}
            onFocus={onFormFieldFocus}
            onBlur={onFormFieldBlur}
            setFormFieldRef={setFormFieldRef}
            fillByClickMode
            renderInput={({ ...rest }) => (
              <FormTextField {...rest} debounce={DECLARATION_FORM_DEBOUNCE} />
            )}
          />
        </NestedTableFormContainer>
      )}
    />
  );
}
